// src/components/resources/quizzes/components/QuestionRenderer.tsx

'use client'

import type { QuizQuestion, QuizFeedback } from '~/lib/resources/types/tools/quiz'
import { QuestionOptions } from '~/components/resources/quizzes/components/question-types/QuestionOptions'
import { TextQuestion } from '~/components/resources/quizzes/components/question-types/TextQuestion'
import { NumberQuestion } from '~/components/resources/quizzes/components/question-types/NumberQuestion'
import { VisualQuestion } from '~/components/resources/quizzes/components/question-types/VisualQuestion'
import { QuestionDescription } from '~/components/resources/quizzes/components/QuestionDescription'
import { PlatformBadge } from '~/components/resources/quizzes/components/PlatformBadge'
import { FeedbackDisplay } from '~/components/resources/quizzes/components/FeedbackDisplay'

interface QuestionRendererProps {
  question: QuizQuestion
  selectedAnswer: string | string[]
  onAnswerSelect: (answer: string | string[]) => void
  disabled?: boolean
  showFeedback?: boolean
  showExplanations?: boolean
  isAnswerCorrect?: boolean
  feedbackPreferences: QuizFeedback
}

type QuestionTypeProps = {
  question: QuizQuestion
  selectedAnswer: string | string[]
  onAnswerSelect: (answer: string | string[]) => void
  disabled: boolean
  showFeedback: boolean
  isAnswerCorrect: boolean
}

export function QuestionRenderer({
  question,
  selectedAnswer,
  onAnswerSelect,
  disabled = false,
  showFeedback = false,
  showExplanations = false,
  isAnswerCorrect = false,
}: QuestionRendererProps): JSX.Element {
  // Create props with known boolean values
  const commonProps: QuestionTypeProps = {
    question,
    selectedAnswer,
    onAnswerSelect,
    disabled: Boolean(disabled),
    showFeedback: Boolean(showFeedback),
    isAnswerCorrect: Boolean(isAnswerCorrect)
  }

  return (
    <div className="space-y-4 sm:space-y-6">
      {/* Platform Badge */}
      {question.platform && (
        <div className="mb-2 sm:mb-4">
          <PlatformBadge platform={question.platform} />
        </div>
      )}

      {/* Question Description - only show if explicitly enabled */}
      {question.description && question.showDescription !== false && (
        <QuestionDescription question={question} />
      )}

      {/* Question Input */}
      {(() => {
        // eslint-disable-next-line @typescript-eslint/switch-exhaustiveness-check
        switch (question.type) {
          case 'text':
            return <TextQuestion {...commonProps} />
          case 'number':
            return <NumberQuestion {...commonProps} />
          case 'visual':
            return <VisualQuestion {...commonProps} />
          case 'multiselect':
          case 'ordered':
          default:
            return <QuestionOptions {...commonProps} />
        }
      })()}

      {/* Explanations */}
      {showExplanations && (
        <FeedbackDisplay
          question={question}
          isCorrect={isAnswerCorrect}
          selectedAnswer={selectedAnswer}
        />
      )}
    </div>
  )
}